import { useState, useEffect } from 'react'
import { fetchDonationSettings, DEFAULT_DONATION_SETTINGS } from '../services/api'

const DonateModal = ({ isOpen, onClose }) => {
  const [mounted, setMounted] = useState(false)
  const [isVisible, setIsVisible] = useState(false)
  const [copiedText, setCopiedText] = useState('')
  const [settings, setSettings] = useState(DEFAULT_DONATION_SETTINGS)

  useEffect(() => {
    if (isOpen) {
      setMounted(true)
      fetchDonationSettings().then((data) => {
        if (data) setSettings(data)
      })
      const t = setTimeout(() => setIsVisible(true), 20)
      return () => clearTimeout(t)
    } else {
      setIsVisible(false)
      const t = setTimeout(() => setMounted(false), 350)
      return () => clearTimeout(t)
    }
  }, [isOpen])

  // Prevent background scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [isOpen])

  // Close on ESC key press
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isOpen) {
        handleStartClose()
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isOpen])

  if (!mounted && !isOpen) return null

  const handleStartClose = () => {
    setIsVisible(false)
    setTimeout(() => {
      onClose()
    }, 320)
  }

  const handleCopy = (text, label) => {
    if (!text) return
    navigator.clipboard.writeText(text)
    setCopiedText(label)
    setTimeout(() => setCopiedText(''), 2000)
  }

  const getCopyStatusIcon = (label) => {
    if (copiedText === label) {
      return (
        <svg className="w-3.5 h-3.5 text-brand-orange-700" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
        </svg>
      )
    }
    return (
      <svg className="w-3.5 h-3.5 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 00-2 2h2a2 2 0 00-2-2M8 5a2 2 0 012-2h2a2 2 0 012 2" />
      </svg>
    )
  }

  return (
    <>
      {/* Backdrop overlay with smooth fade transition */}
      <div
        className={`fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-xs transition-opacity duration-350 ease-in-out ${
          isVisible ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={handleStartClose}
      />

      {/* Slide-Up / Slide-Down Bottom-Attached Container */}
      <div
        className={`fixed bottom-0 left-0 right-0 z-50 flex justify-center pointer-events-none transition-transform duration-350 ease-[cubic-bezier(0.16,1,0.3,1)] transform ${
          isVisible ? 'translate-y-0' : 'translate-y-full'
        }`}
        data-lenis-prevent
      >
        <div
          className="bg-white rounded-none w-full max-w-4xl lg:max-w-5xl 2xl:max-w-6xl shadow-2xl border-t-2 border-x border-slate-300 overflow-hidden flex flex-col max-h-[88vh] text-left pointer-events-auto mb-0"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Modal Header */}
          <div className="bg-[#FAF6F2] border-b border-orange-200/80 px-5 py-4 sm:px-8 sm:py-5 flex items-center justify-between shrink-0 rounded-none">
            <div className="space-y-0.5">
              <h3 className="text-xl sm:text-2xl font-display font-bold text-slate-900 leading-snug">
                Make a Life-Saving Contribution
              </h3>
              <p className="text-xs sm:text-sm text-slate-600 font-medium">
                Support Chetak Animal Welfare Trust through Tax-Exempt UPI &amp; Direct Bank Transfer
              </p>
            </div>
            <button
              type="button"
              onClick={handleStartClose}
              className="w-9 h-9 bg-slate-200/80 hover:bg-slate-300 text-slate-700 hover:text-slate-950 flex items-center justify-center font-bold text-base transition-all cursor-pointer rounded-none border border-slate-300"
              title="Close"
            >
              ✕
            </button>
          </div>

          {/* Modal Body */}
          <div className="p-5 sm:p-8 overflow-y-auto no-scrollbar [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none] flex-1 bg-white">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-6 lg:gap-8 items-start">
              
              {/* Left Column: QR Code Box */}
              <div className="md:col-span-5 flex flex-col items-center justify-center p-6 bg-slate-50 border border-slate-200 space-y-4 rounded-none">
                <span className="px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-brand-orange-700 bg-brand-orange-50 border border-brand-orange-200 rounded-none">
                  Scan UPI QR Code
                </span>

                {/* QR Code Graphic wrapper */}
                <div className="relative p-4 bg-white border border-slate-200 shadow-xs flex flex-col items-center justify-center rounded-none">
                  <div className="relative w-44 h-44 bg-slate-50 flex items-center justify-center overflow-hidden rounded-none">
                    {settings.qr_image_url ? (
                      <img
                        src={settings.qr_image_url}
                        alt="Donation UPI QR Code"
                        className="w-full h-full object-contain p-1"
                        onError={(e) => {
                          e.target.style.display = 'none'
                        }}
                      />
                    ) : (
                      /* Default Premium QR Code Graphic */
                      <svg className="w-36 h-36 text-slate-800" viewBox="0 0 100 100" fill="currentColor">
                        <rect x="5" y="5" width="25" height="25" fill="none" stroke="currentColor" strokeWidth="6" />
                        <rect x="11" y="11" width="13" height="13" />
                        <rect x="70" y="5" width="25" height="25" fill="none" stroke="currentColor" strokeWidth="6" />
                        <rect x="76" y="11" width="13" height="13" />
                        <rect x="5" y="70" width="25" height="25" fill="none" stroke="currentColor" strokeWidth="6" />
                        <rect x="11" y="76" width="13" height="13" />
                        
                        <rect x="35" y="5" width="6" height="12" />
                        <rect x="45" y="5" width="18" height="6" />
                        <rect x="35" y="23" width="12" height="6" />
                        <rect x="53" y="17" width="10" height="12" />
                        
                        <rect x="5" y="35" width="12" height="6" />
                        <rect x="5" y="47" width="6" height="18" />
                        <rect x="17" y="47" width="12" height="6" />
                        <rect x="23" y="59" width="6" height="6" />
                        
                        <rect x="35" y="35" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="4" />
                        <rect x="42" y="42" width="4" height="4" />
                        
                        <rect x="60" y="35" width="12" height="6" />
                        <rect x="78" y="35" width="18" height="6" />
                        <rect x="78" y="47" width="6" height="18" />
                        <rect x="89" y="47" width="6" height="6" />
                        <rect x="60" y="53" width="6" height="12" />
                        <rect x="70" y="59" width="12" height="6" />
                        
                        <rect x="35" y="70" width="6" height="18" />
                        <rect x="47" y="70" width="18" height="6" />
                        <rect x="47" y="82" width="12" height="12" />
                        <rect x="70" y="76" width="6" height="18" />
                        <rect x="82" y="76" width="12" height="6" />
                        <rect x="82" y="88" width="12" height="6" />
                      </svg>
                    )}

                    {!settings.qr_image_url && (
                      <div className="absolute inset-0 m-auto w-10 h-10 bg-white rounded-none flex items-center justify-center shadow-md border border-slate-300">
                        <span className="text-xs font-black tracking-tighter text-brand-orange-700">CAWT</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* UPI ID & Copy button */}
                <div className="text-center space-y-1 w-full">
                  <div className="flex items-center justify-center gap-2 p-2 bg-white border border-slate-200 rounded-none">
                    <span className="text-xs font-bold text-slate-700">UPI:</span>
                    <span className="font-mono text-brand-orange-700 text-xs sm:text-sm font-bold">{settings.upi_id || 'cawt.trust@ybl'}</span>
                    <button
                      onClick={() => handleCopy(settings.upi_id || 'cawt.trust@ybl', 'upi')}
                      className="p-1 text-slate-500 hover:text-slate-800 transition-colors cursor-pointer"
                      title="Copy UPI ID"
                    >
                      {getCopyStatusIcon('upi')}
                    </button>
                  </div>
                  <p className="text-[11px] text-slate-500">{settings.subtext || 'Scan with GPay, PhonePe, Paytm, or BHIM'}</p>
                </div>
              </div>

              {/* Right Column: Bank Details & Tax Note */}
              <div className="md:col-span-7 space-y-4">
                <span className="px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-brand-orange-700 bg-brand-orange-50 border border-brand-orange-200 inline-block rounded-none">
                  Direct NEFT / RTGS Bank Transfer
                </span>

                <div className="p-5 bg-slate-50 border border-slate-200 space-y-3 text-xs sm:text-sm rounded-none">
                  <div className="flex justify-between items-center pb-2.5 border-b border-slate-200">
                    <span className="text-xs text-slate-500 font-bold uppercase tracking-wider">Account Name</span>
                    <span className="font-bold text-slate-900 text-right">{settings.account_name || 'Companion Animal Welfare Trust'}</span>
                  </div>

                  <div className="flex justify-between items-center pb-2.5 border-b border-slate-200">
                    <span className="text-xs text-slate-500 font-bold uppercase tracking-wider">Bank Name</span>
                    <span className="font-bold text-slate-900 text-right">{settings.bank_name || 'State Bank of India'}</span>
                  </div>

                  <div className="flex justify-between items-center pb-2.5 border-b border-slate-200">
                    <span className="text-xs text-slate-500 font-bold uppercase tracking-wider">Account Number</span>
                    <div className="flex items-center gap-2">
                      <span className="font-mono font-bold text-slate-900 text-sm">{settings.account_number || '98765432101'}</span>
                      <button
                        onClick={() => handleCopy(settings.account_number || '98765432101', 'acc')}
                        className="p-1 text-slate-500 hover:text-slate-800 transition-colors cursor-pointer"
                        title="Copy Account Number"
                      >
                        {getCopyStatusIcon('acc')}
                      </button>
                    </div>
                  </div>

                  <div className="flex justify-between items-center pb-2.5 border-b border-slate-200">
                    <span className="text-xs text-slate-500 font-bold uppercase tracking-wider">IFSC Code</span>
                    <div className="flex items-center gap-2">
                      <span className="font-mono font-bold text-slate-900 text-sm">{settings.ifsc_code || 'SBIN0001234'}</span>
                      <button
                        onClick={() => handleCopy(settings.ifsc_code || 'SBIN0001234', 'ifsc')}
                        className="p-1 text-slate-500 hover:text-slate-800 transition-colors cursor-pointer"
                        title="Copy IFSC"
                      >
                        {getCopyStatusIcon('ifsc')}
                      </button>
                    </div>
                  </div>

                  {settings.branch_name && (
                    <div className="flex justify-between items-center pb-1">
                      <span className="text-xs text-slate-500 font-bold uppercase tracking-wider">Branch</span>
                      <span className="font-semibold text-slate-700 text-right">{settings.branch_name}</span>
                    </div>
                  )}
                </div>

                <div className="p-3.5 bg-orange-50/70 border border-orange-200 text-xs text-slate-700 leading-relaxed rounded-none">
                  💡 <strong className="text-slate-900">Tax Exemption:</strong> All donations are exempt from tax under Section 80G. Please mention your PAN in transfer notes for automated receipt generation.
                </div>
              </div>

            </div>
          </div>

          {/* Modal Footer */}
          <div className="px-5 py-3.5 sm:px-8 sm:py-4 bg-slate-50 border-t border-slate-200 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-slate-600 rounded-none">
            <span className="flex items-center gap-1.5 font-medium">
              <svg className="w-4 h-4 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
              100% Direct Contribution to Animal Rescue &amp; Medical Care
            </span>
            <button
              type="button"
              onClick={handleStartClose}
              className="px-6 py-2 bg-slate-200 hover:bg-slate-300 text-slate-800 font-bold text-xs uppercase tracking-wider transition-all cursor-pointer rounded-none border border-slate-300"
            >
              Close
            </button>
          </div>

        </div>
      </div>
    </>
  )
}

export default DonateModal
